// frontend/modules/template/views/ContextTransformationView.tsx
import React from 'react';
import {
    Typography,
    Button,
    Space,
    Row,
    Col
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ContextTransformationForm } from '../components/ContextTransformationForm';
import { TransformationResultPreview } from '../components/TransformationResultPreview';
import { useContextTransformation } from '../hooks/useContextTransformation';
import { ContextTransformationRequest } from '../types/context-transformation.types';

const { Title } = Typography;

export const ContextTransformationView: React.FC = () => {
    const {
        transformationResult,
        isTransforming,
        transformAndProcess,
        testNotificationConfiguration,
        resetTransformation
    } = useContextTransformation();

    const handleSubmit = async (values: ContextTransformationRequest) => {
        try {
            console.log('Submitting transformation request:', values);

            // If we have a transformation result and selected contact groups,
            // submit with the selected groups for notification testing
            if (transformationResult && values.selectedContactGroups) {
                await testNotificationConfiguration({
                    ...values,
                    selectedContactGroups: values.selectedContactGroups
                });
                return;
            }

            // Submit for transformation and processing
            await transformAndProcess({
                ...values,
                // Ensure contextJson is a string (the form provides it as string already)
                contextJson: values.contextJson
            });
        } catch (error) {
            console.error('Error during transformation:', error);
            // Error handling is done in the hook
        }
    };

    const handleReset = () => {
        resetTransformation();
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <Space className="mb-4">
                        <Link to="/notifications/context-scripts">
                            <Button icon={<ArrowLeftOutlined />}>
                                Back to Scripts
                            </Button>
                        </Link>
                        {transformationResult && (
                            <Button onClick={handleReset}>
                                Reset
                            </Button>
                        )}
                    </Space>

                    <Title level={2} className="m-0">
                        Context Transformation & Template Processing
                    </Title>
                    <Typography.Text type="secondary" className="text-lg">
                        Transform context data using JavaScript scripts and process notification templates
                    </Typography.Text>
                </div>

                {/* Main Content */}
                <Row gutter={[24, 24]} className="min-h-[600px]">
                    {/* Form Column */}
                    <Col xs={24} lg={transformationResult ? 10 : 16} xl={transformationResult ? 8 : 12}>
                        <div className="sticky top-6">
                            <ContextTransformationForm
                                onSubmit={handleSubmit}
                                loading={isTransforming}
                                transformationResult={transformationResult}
                            />
                        </div>
                    </Col>

                    {/* Results Column */}
                    <Col xs={24} lg={transformationResult ? 14 : 8} xl={transformationResult ? 16 : 12}>
                        <TransformationResultPreview
                            result={transformationResult}
                            loading={isTransforming}
                        />

                        {!transformationResult && !isTransforming && (
                            <div className="h-full flex items-center justify-center bg-white rounded-lg border-2 border-dashed border-gray-300 min-h-[400px]">
                                <div className="text-center text-gray-500">
                                    <div className="text-4xl mb-4">⚙️</div>
                                    <div className="text-lg font-medium mb-2">Ready to Transform</div>
                                    <div>Select a script and provide context to see the transformation results</div>
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </div>
    );
};